import type { EndUserPages } from '@aurora/shared-types/pages/enums';
import { EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import { getLog } from '@aurora/shared-utils/log';
import React from 'react';
import { Pagination, useClassNameMapper } from 'react-bootstrap';
import { SharedComponent } from '../../../../enums';
import Icons from '../../../../icons';
import type { EndUserRouteAndParams } from '../../../../routes/endUserRoutes';
import useEndUserRoutes from '../../../../routes/useEndUserRoutes';
import useTranslation from '../../../useTranslation';
import { IconSize } from '../../Icon/enums';
import Icon from '../../Icon/Icon';
import { PagerPosition } from '../enums';
import PagerPreviousNextLinkableLink from '../PagerPreviousNextLinkableLInk/PagerPreviousNextLinkableLink';
import type { PagerPreviousNextLinkableProps } from '../types';
import localStyles from './PagerPreviousNextLinkable.module.pcss';

const log = getLog(module);

/**
 * The 'previousNextLinkable' variant of the pager. It displays '<', '>', 'Go to first page' links that are linkable.
 *
 * @author Abhijith.Be, Willi Hyde
 */
const PagerPreviousNextLinkable: React.FC<
  React.PropsWithChildren<PagerPreviousNextLinkableProps>
> = ({ pageInfo, className, position = PagerPosition.CENTER }) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    SharedComponent.PAGER_PREVIOUS_NEXT_LINKABLE
  );
  const usePaging = pageInfo.hasNextPage || pageInfo.hasPreviousPage;
  const incompletePageInfo: boolean = pageInfo.hasPreviousPage === undefined;
  const { router, Link } = useEndUserRoutes();
  const { params, route } = router.getCurrentRouteAndParams();

  if (incompletePageInfo) {
    log.error(
      'The PreviousNextLinkable pager requires a value for hasPreviousPage. If using an EntityView query make sure to add the useFullPageInfo property'
    );
  }

  if (textLoading || incompletePageInfo) {
    return null;
  }

  return (
    usePaging && (
      <nav className={cx(`d-flex flex-column lia-g-row-gap-25 align-items-${position}`, className)}>
        <Pagination data-testid="PagerPreviousNextLinkable.Pagination">
          <PagerPreviousNextLinkableLink
            ariaLabel={formatMessage('previous')}
            testId="PagerPreviousNextLinkable.Previous"
            query={{
              ...router.getQueryParams(),
              [EndUserQueryParams.AFTER]: null,
              [EndUserQueryParams.BEFORE]: pageInfo.startCursor
            }}
            isFirstOrLastPage={!pageInfo.hasPreviousPage}
          >
            <Icon icon={Icons.ChevronLeftIcon} size={IconSize.PX_16} />
          </PagerPreviousNextLinkableLink>
          <PagerPreviousNextLinkableLink
            ariaLabel={formatMessage('next')}
            testId="PagerPreviousNextLinkable.Next"
            query={{
              ...router.getQueryParams(),
              [EndUserQueryParams.BEFORE]: null,
              [EndUserQueryParams.AFTER]: pageInfo.endCursor
            }}
            isFirstOrLastPage={!pageInfo.hasNextPage}
          >
            <Icon icon={Icons.ChevronRightIcon} size={IconSize.PX_16} />
          </PagerPreviousNextLinkableLink>
        </Pagination>
        {pageInfo.hasPreviousPage ? (
          <Link<EndUserRouteAndParams<EndUserPages>> route={route} params={params}>
            <a>{formatMessage('firstPage')}</a>
          </Link>
        ) : (
          <span className={cx('lia-link-disabled')}>{formatMessage('firstPage')}</span>
        )}
      </nav>
    )
  );
};

export default PagerPreviousNextLinkable;
